import { DescriptionCell, LanguagesCell, MarketsCell, SlugCell, TitleCell } from '@app/components/pagesList/cells';
import { PageOptionsDropdown } from '@app/components/pagesList/PageOptionsDropdown';
import { languagesSorter } from '@app/logic/cms/pagesList';
import { ACTION_COLUMN_WIDTH, getUpdatedStateValue, renderStandardCell } from '@app/utils/table';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Cms, Id } from 'typings';

import { usePaginationHandler } from '../table/usePaginationHandler';
import { useTable } from '../table/useTable';
import { useTableColumnsConfiguration } from '../table/useTableColumnsConfiguration';
import { usePageScrolling } from '../usePageScrolling';
import { useUrlSort } from '../useUrlSort';

interface Props {
  pages: Cms.Page[];
  statusFilter: Cms.PageStatus | '';
  setEditingPage: (id: Id) => void;
  openSharePage: (id: Id) => void;
  duplicatePage: ({ pageId }: { pageId: Id }) => void;
}

const columnHelper = createColumnHelper<Cms.Page>();

export const usePagesListTable = ({ pages, statusFilter, setEditingPage, openSharePage, duplicatePage }: Props) => {
  const [sort, setSort] = useUrlSort();
  const [sorting, setSorting] = React.useState<SortingState>(sort !== null ? [sort] : []);
  const [page, setPage] = React.useState(0);
  const { t } = useTranslation(['common', 'cms']);
  const { scrollToTopDelay } = usePageScrolling();

  const handleSortingChange: OnChangeFn<SortingState> = updaterOrValue => {
    const newSorting = getUpdatedStateValue(updaterOrValue, sorting);

    setSort(newSorting[0] ?? null);
    setSorting(updaterOrValue);
  };

  const handlePageChange = React.useCallback(
    (nextPage: number) => {
      setPage(nextPage);
      scrollToTopDelay();
    },
    [scrollToTopDelay],
  );

  const { columnsVisibility } = useTableColumnsConfiguration({ tableName: 'pages' });
  const { pagination, handlePaginationChange } = usePaginationHandler({ onPageChange: handlePageChange, page });

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: info => <TitleCell text={info.getValue()} row={info.row.original} />,
        enableHiding: false,
        header: t('common:title'),
      }),
      columnHelper.accessor('slug', {
        cell: info => <SlugCell slug={info.getValue()} row={info.row.original} />,
        enableHiding: false,
        header: t('common:uri'),
      }),
      columnHelper.accessor('languages', {
        cell: info => <LanguagesCell languages={info.getValue() || []} row={info.row.original} filter={statusFilter} />,
        header: t('common:language_other'),
        meta: {
          width: 132,
        },
        sortingFn: (rowA, rowB) => languagesSorter(rowA.original, rowB.original),
      }),
      columnHelper.accessor('markets', {
        cell: info => <MarketsCell marketsData={info.getValue()} row={info.row.original} />,
        header: t('cms:market_other'),
        meta: {
          isCollapsible: true,
          minWidth: 132,
          width: '100%',
        },
      }),
      columnHelper.accessor('createdDate', {
        cell: renderStandardCell,
        header: t('common:created'),
      }),
      columnHelper.accessor('lastModifiedDate', {
        cell: renderStandardCell,
        header: t('common:last_modified'),
      }),
      columnHelper.accessor('lastPublishedDate', {
        cell: renderStandardCell,
        header: t('common:last_published'),
      }),
      columnHelper.accessor('description', {
        cell: info => <DescriptionCell description={info.getValue()} row={info.row.original} />,
        header: t('common:description'),
      }),
      columnHelper.display({
        cell: info => {
          const row = info.row.original;

          return (
            <PageOptionsDropdown page={row} setEditingPage={setEditingPage} openSharePage={openSharePage} duplicatePage={duplicatePage} />
          );
        },
        enableHiding: false,
        id: 'actions',
        meta: {
          skeletonType: 'actions',
          width: ACTION_COLUMN_WIDTH,
        },
      }),
    ],
    [t, statusFilter, setEditingPage, openSharePage, duplicatePage],
  );

  const table = useTable(
    {
      autoResetPageIndex: false,
      columns,
      data: pages,
      getCoreRowModel: getCoreRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onPaginationChange: handlePaginationChange,
      onSortingChange: handleSortingChange,
      state: {
        pagination,
        sorting,
      },
    },
    columnsVisibility,
  );

  return {
    columnsVisibility,
    pagination,
    table,
  };
};
